import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Pie, Doughnut, Radar } from 'react-chartjs-2';
import { apiService } from '../services/api';
import { WeeklyCheckIn, WeeklyCheckInStats } from '../types';
import { TrendingUp, TrendingDown, Minus, Target, Activity, Scale, Heart, Star } from 'lucide-react';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Progress: React.FC = () => {
  const [weeklyCheckIns, setWeeklyCheckIns] = useState<WeeklyCheckIn[]>([]);
  const [stats, setStats] = useState<WeeklyCheckInStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'4weeks' | '8weeks' | '12weeks'>('8weeks');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [checkInsResponse, statsResponse] = await Promise.all([
          apiService.getWeeklyCheckIns(1), // Get first page of check-ins
          apiService.getWeeklyCheckInStats()
        ]);
        
        // Fetch detailed data for each check-in
        const detailedCheckIns = checkInsResponse.results.length > 0 
          ? await Promise.all(
              checkInsResponse.results.map(summary => 
                apiService.getWeeklyCheckIn(summary.id)
              )
            )
          : [];
        
        setWeeklyCheckIns(detailedCheckIns);
        setStats(statsResponse);
      } catch (err) {
        setError('Failed to load progress data');
        console.error('Error fetching progress data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter check-ins based on time range
  const getFilteredCheckIns = () => {
    const weeks = timeRange === '4weeks' ? 4 : timeRange === '8weeks' ? 8 : 12;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - (weeks * 7));
    
    return weeklyCheckIns
      .filter(checkIn => new Date(checkIn.submitted_at) >= cutoffDate)
      .sort((a, b) => new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime());
  };

  const filteredCheckIns = getFilteredCheckIns();

  // Chart data preparation
  const consistencyTrendsData = {
    labels: filteredCheckIns.map(checkIn => 
      new Date(checkIn.week_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    ),
    datasets: [
      {
        label: 'Nutrition Consistency',
        data: filteredCheckIns.map(checkIn => checkIn.nutrition_consistency),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Training Consistency',
        data: filteredCheckIns.map(checkIn => checkIn.training_consistency),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Step Count Consistency',
        data: filteredCheckIns.map(checkIn => checkIn.step_count_consistency),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const overallGradeData = {
    labels: filteredCheckIns.map(checkIn => 
      new Date(checkIn.week_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    ),
    datasets: [
      {
        label: 'Overall Grade',
        data: filteredCheckIns.map(checkIn => checkIn.overall_grade),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgb(245, 158, 11)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
      },
    ],
  };

  const trafficLightData = {
    labels: ['Green (Great Week)', 'Amber (Okay Week)', 'Red (Need Help)'],
    datasets: [
      {
        data: stats ? [
          stats.traffic_light_distribution.green,
          stats.traffic_light_distribution.amber,
          stats.traffic_light_distribution.red,
        ] : [0, 0, 0],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
        ],
        borderColor: [
          'rgb(34, 197, 94)',
          'rgb(245, 158, 11)',
          'rgb(239, 68, 68)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const progressAssessmentData = {
    labels: ['Progressed', 'Stayed the Same', 'Regressed'],
    datasets: [
      {
        label: 'Number of Weeks',
        data: stats ? [
          stats.progress_distribution.progressed,
          stats.progress_distribution.stayed_same,
          stats.progress_distribution.regressed,
        ] : [0, 0, 0],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(156, 163, 175, 0.8)',
          'rgba(239, 68, 68, 0.8)',
        ],
        borderColor: [
          'rgb(34, 197, 94)',
          'rgb(156, 163, 175)',
          'rgb(239, 68, 68)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const weightData = {
    labels: filteredCheckIns
      .filter(checkIn => checkIn.weight_kg)
      .map(checkIn => 
        new Date(checkIn.week_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      ),
    datasets: [
      {
        label: 'Weight (kg)',
        data: filteredCheckIns
          .filter(checkIn => checkIn.weight_kg)
          .map(checkIn => checkIn.weight_kg),
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgb(99, 102, 241)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
      },
    ],
  };

  const confidenceData = {
    labels: filteredCheckIns.map(checkIn => 
      new Date(checkIn.week_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    ),
    datasets: [
      {
        label: 'Self-Confidence Rating',
        data: filteredCheckIns.map(checkIn => checkIn.self_confidence_rating),
        borderColor: 'rgb(236, 72, 153)',
        backgroundColor: 'rgba(236, 72, 153, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgb(236, 72, 153)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
      },
    ],
  };

  // Radar chart for current week's consistency
  const currentWeekCheckIn = filteredCheckIns[filteredCheckIns.length - 1];
  const radarData = currentWeekCheckIn ? {
    labels: ['Nutrition', 'Training', 'Step Count', 'Overall Grade', 'Self-Confidence'],
    datasets: [
      {
        label: 'Current Week',
        data: [
          currentWeekCheckIn.nutrition_consistency,
          currentWeekCheckIn.training_consistency,
          currentWeekCheckIn.step_count_consistency,
          currentWeekCheckIn.overall_grade,
          currentWeekCheckIn.self_confidence_rating,
        ],
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
      },
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const lineChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const weightChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: false,
      },
    },
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-80 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Progress Dashboard</h1>
        <p className="text-gray-600 mb-4">Track your fitness journey and see your improvements over time</p>
        
        {/* Time Range Selector */}
        <div className="flex items-center space-x-4 mb-6">
          <span className="text-sm font-medium text-gray-700">Time Range:</span>
          <div className="flex space-x-2">
            {[
              { value: '4weeks', label: '4 Weeks' },
              { value: '8weeks', label: '8 Weeks' },
              { value: '12weeks', label: '12 Weeks' },
            ].map((range) => (
              <button
                key={range.value}
                onClick={() => setTimeRange(range.value as any)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  timeRange === range.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center">
                <Activity className="h-8 w-8 text-blue-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Check-ins</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_checkins}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center">
                <Star className="h-8 w-8 text-yellow-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Average Grade</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.average_overall_grade}/5</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Progress Weeks</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.progress_distribution.progressed}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center">
                <Target className="h-8 w-8 text-purple-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Green Weeks</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.traffic_light_distribution.green}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {filteredCheckIns.length === 0 && !loading && (
        <div className="bg-white p-8 rounded-lg shadow border text-center">
          <div className="text-gray-400 mb-4">
            <Activity className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Progress Data Yet</h3>
          <p className="text-gray-600 mb-4">
            Start tracking your progress by completing your first weekly check-in.
          </p>
          <button 
            onClick={() => window.location.href = '/check-ins'}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Complete Check-in
          </button>
        </div>
      )}

      {/* Charts Grid */}
      {filteredCheckIns.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Consistency Trends */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Consistency Trends</h3>
          <div className="h-80">
            <Line data={consistencyTrendsData} options={lineChartOptions} />
          </div>
        </div>

        {/* Overall Grade Progress */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Grade Progress</h3>
          <div className="h-80">
            <Line data={overallGradeData} options={lineChartOptions} />
          </div>
        </div>

        {/* Traffic Light Distribution */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Week Performance Distribution</h3>
          <div className="h-80">
            <Doughnut data={trafficLightData} options={chartOptions} />
          </div>
        </div>

        {/* Progress Assessment */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Assessment</h3>
          <div className="h-80">
            <Bar data={progressAssessmentData} options={chartOptions} />
          </div>
        </div>

        {/* Weight Tracking */}
        {weightData.datasets[0].data.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Weight Progress</h3>
            <div className="h-80">
              <Line data={weightData} options={weightChartOptions} />
            </div>
          </div>
        )}

        {/* Self-Confidence Trends */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Self-Confidence Trends</h3>
          <div className="h-80">
            <Line data={confidenceData} options={lineChartOptions} />
          </div>
        </div>

        {/* Current Week Radar Chart */}
        {radarData && currentWeekCheckIn && (
          <div className="bg-white p-6 rounded-lg shadow border lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Week Performance Overview</h3>
            <div className="h-80">
              <Radar data={radarData} options={radarOptions} />
            </div>
          </div>
        )}
      </div>
      )}

      {/* Insights Section */}
      {filteredCheckIns.length > 0 && (
        <div className="mt-8 bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(() => {
              const latest = filteredCheckIns[filteredCheckIns.length - 1];
              const previous = filteredCheckIns[filteredCheckIns.length - 2];
              
              if (!latest || !previous) return null;
              
              const gradeChange = latest.overall_grade - previous.overall_grade;
              const confidenceChange = latest.self_confidence_rating - previous.self_confidence_rating;
              
              return (
                <>
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div className={`p-2 rounded-full ${
                      gradeChange > 0 ? 'bg-green-100 text-green-600' :
                      gradeChange < 0 ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {gradeChange > 0 ? <TrendingUp className="h-4 w-4" /> :
                       gradeChange < 0 ? <TrendingDown className="h-4 w-4" /> :
                       <Minus className="h-4 w-4" />}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Overall Grade</p>
                      <p className="text-sm text-gray-500">
                        {gradeChange > 0 ? `+${gradeChange}` : gradeChange} from last week
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div className={`p-2 rounded-full ${
                      confidenceChange > 0 ? 'bg-green-100 text-green-600' :
                      confidenceChange < 0 ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {confidenceChange > 0 ? <TrendingUp className="h-4 w-4" /> :
                       confidenceChange < 0 ? <TrendingDown className="h-4 w-4" /> :
                       <Minus className="h-4 w-4" />}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Self-Confidence</p>
                      <p className="text-sm text-gray-500">
                        {confidenceChange > 0 ? `+${confidenceChange}` : confidenceChange} from last week
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div className={`p-2 rounded-full ${
                      latest.traffic_light_rating === 'green' ? 'bg-green-100 text-green-600' :
                      latest.traffic_light_rating === 'amber' ? 'bg-yellow-100 text-yellow-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      <Target className="h-4 w-4" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">This Week</p>
                      <p className="text-sm text-gray-500 capitalize">
                        {latest.traffic_light_rating} week
                      </p>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Progress; 